import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { GridApi } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';

import useLocalStorage, {
  namespacedKey,
} from '@/components/hooks/useLocalStorage';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { repairFilterModel } from '@/utils/repairFilterModel';

type WithLocalStorageProps = {
  children: React.ReactElement;
  storageKey: string;
  delay?: number;
  onSave?: (api: GridApi) => void;
  onLoad?: (api: GridApi) => void;
  clearButton?: boolean;
  clearFabClass?: string;
  saveFabClass?: string;
};

type GridInstance = { api: GridApi; columnApi: any };

const DEFAULT_DELAY = 500;
const DEF_CLEAR_POS = '-top-0 right-0';
const DEF_SAVE_POS = '-top-0 right-14';

export const WithLocalStorage = ({
  children,
  storageKey,
  delay = DEFAULT_DELAY,
  onSave,
  onLoad,
  clearButton = false,
  clearFabClass,
  saveFabClass,
}: WithLocalStorageProps) => {
  const [gridInstance, setGridInstance] = useState<GridInstance | null>(null);
  const [gridState, setGridState] = useLocalStorage(storageKey, null);
  const onLoadRef = useRef(onLoad);
  const isFixAndOrFilters = useFeatureFlag('Fix_Custom_View_And_Or_Filters');

  const [showClear, setShowClear] = useState(false);
  const [showSave, setShowSave] = useState(false);

  useEffect(() => {
    onLoadRef.current = onLoad;
  }, [onLoad]);

  const saveLayout = useCallback(() => {
    if (!gridInstance) return;
    const { api } = gridInstance;
    setGridState({
      columnState: api.getColumnState(),
      filterModel: api.getFilterModel(),
    });
  }, [gridInstance, setGridState]);

  const loadLayout = useCallback(() => {
    if (!gridInstance) return;
    const raw = localStorage.getItem(namespacedKey(storageKey));
    if (!raw) {
      onLoadRef.current?.(gridInstance.api);
      return;
    }

    try {
      const s = JSON.parse(raw);
      const savedColumnState = Array.isArray(s?.columnState)
        ? s.columnState
        : [];

      gridInstance.api.applyColumnState({
        state: savedColumnState,
        applyOrder: true,
      });
      gridInstance.api.setFilterModel(
        isFixAndOrFilters ? repairFilterModel(s.filterModel) : s.filterModel,
      );
    } catch {
    } finally {
      onLoadRef.current?.(gridInstance.api);
    }
  }, [gridInstance, storageKey, isFixAndOrFilters]);

  const updateFabVisibility = useCallback(() => {
    if (!gridInstance) return;

    const api = gridInstance.api;
    const colState = api.getColumnState();

    const hasFilters = api.isAnyFilterPresent();
    const hasSort = colState.some((c) => c.sort);
    const hasHidden = colState.some((c) => c.hide);

    setShowClear(hasFilters || hasSort || hasHidden);
    setShowSave(hasFilters || hasSort);
  }, [gridInstance]);

  useEffect(() => {
    if (!gridInstance) return;

    loadLayout();
    updateFabVisibility();

    const { api } = gridInstance;
    const on = (e: string, fn: () => void) => {
      api.addEventListener(e, fn);
      return () => {
        if (!api.isDestroyed?.()) api.removeEventListener(e, fn);
      };
    };

    const changed = () => {
      saveLayout();
      updateFabVisibility();
    };

    const off = [
      on('gridReady', loadLayout),
      on('columnMoved', changed),
      on('columnResized', changed),
      on('columnVisible', changed),
      on('columnPinned', changed),
      on('filterChanged', changed),
      on('sortChanged', changed),
    ];
    return () => off.forEach((f) => f());
  }, [gridInstance, loadLayout, saveLayout, updateFabVisibility]);

  const clearAll = () => {
    if (!gridInstance) return;
    localStorage.removeItem(namespacedKey(storageKey));
    gridInstance.api.setFilterModel(null);
    gridInstance.columnApi.applyColumnState({
      defaultState: { sort: null, hide: false },
    });
    updateFabVisibility();
  };

  const saveThenReset = () => {
    if (!gridInstance || !onSave) return;
    onSave(gridInstance.api);
    const { api, columnApi } = gridInstance;
    api.setFilterModel(null);
    const cleared = columnApi.getColumnState().map((c: any) => ({
      ...c,
      sort: null,
      sortIndex: null,
    }));
    columnApi.applyColumnState({ state: cleared, applyOrder: false });
    updateFabVisibility();
  };

  const chainReady =
    (orig?: (p: GridInstance) => void) => (p: GridInstance) => {
      setTimeout(() => setGridInstance(p), delay);
      orig?.(p);
    };

  const unwrapMemo = (type: any): any =>
    type?.$$typeof === Symbol.for('react.memo') ? unwrapMemo(type.type) : type;

  const looksLikeAgGridReact = (type: any): boolean =>
    type === AgGridReact ||
    type?.displayName === 'AgGridReact' ||
    type?.forwardsAgGridProps === true ||
    !!type?.prototype?.setGridApi;

  const inject = (el: React.ReactElement): React.ReactElement => {
    const directType = el.type;
    const innerType = unwrapMemo(directType);

    if (looksLikeAgGridReact(directType) || looksLikeAgGridReact(innerType)) {
      return React.cloneElement(el, {
        ...el.props,
        onGridReady: chainReady(el.props.onGridReady),
      });
    }

    const patchedChildren = React.Children.map(el.props.children, (c) =>
      React.isValidElement(c) ? inject(c) : c,
    );

    return typeof el.type === 'string'
      ? React.cloneElement(el, { ...el.props, children: patchedChildren })
      : React.cloneElement(el, { ...el.props, children: patchedChildren });
  };

  const clearPos = clearFabClass ?? DEF_CLEAR_POS;
  const savePos = saveFabClass ?? DEF_SAVE_POS;

  return (
    <div className="relative h-full">
      {inject(children)}

      {showClear && clearButton && (
        <button
          onClick={clearAll}
          title="Clear filters, sort & hidden columns"
          className={`absolute ${clearPos} z-50 h-11 w-11 rounded-full border-0 bg-gradient-to-br from-red-500 to-red-700 text-lg text-white hover:bg-red-700 hover:shadow-2xl focus:outline-none`}
          aria-label="Clear filters, sort & hidden columns"
        >
          ✕
        </button>
      )}

      {showSave && onSave && (
        <button
          onClick={saveThenReset}
          title="Save order, then clear filters & sort"
          className={`absolute ${savePos} z-50 h-11 w-11 rounded-full border-0 bg-blue-700 text-base text-white hover:bg-blue-800 hover:shadow-2xl focus:outline-none`}
          aria-label="Save order then reset grid"
        >
          <i className="pi pi-check" style={{ fontSize: '1rem' }} />
        </button>
      )}
    </div>
  );
};
